import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Switch } from '@headlessui/react'
import { EyeIcon, EyeOffIcon, UserGroupIcon, LockClosedIcon, CheckCircleIcon } from '@heroicons/react/solid'

import classNames from '@/utils/classNamesLocal'
import { useGlobalState } from '@/state'
import useQuery from '@/hooks/useQuery'
import Card from '@/shared/Card'

const VISIBILITY = [
  { value: 'public', label: 'Public', icon: EyeIcon, blurb: 'Anyone can find and view your profile.' },
  { value: 'followers', label: 'Followers only', icon: UserGroupIcon, blurb: 'Only people who follow you can view it. Hidden from search.' },
  { value: 'private', label: 'Private', icon: EyeOffIcon, blurb: 'Nobody but you.' }
]

const Toggle = ({ checked, onChange, label, hint, locked }) => (
  <Switch.Group as='div' className='flex items-center justify-between gap-x-3 py-3'>
    <Switch.Label as='span' className='flex flex-col'>
      <span className='flex items-center gap-x-1 text-sm font-medium text-gray-900 dark:text-gray-200'>{label}{ locked && <LockClosedIcon className='h-3.5 w-3.5 text-cccpurple' /> }</span>
      <span className='text-xs text-gray-500 dark:text-gray-400'>{hint}</span>
    </Switch.Label>
    <Switch checked={!!checked} onChange={onChange} disabled={locked}
      className={classNames(checked ? 'bg-cccpurple' : 'bg-gray-300 dark:bg-gray-500', locked && 'cursor-not-allowed opacity-50', 'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-cccpurple focus:ring-offset-2')}>
      <span aria-hidden='true' className={classNames(checked ? 'translate-x-5' : 'translate-x-0', 'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition')} />
    </Switch>
  </Switch.Group>
)

// Each change saves immediately and says so.
const ProfileOptions = () => {
  const [user, setUser] = useGlobalState('userContainerUser')
  const { putpostRequest } = useQuery()
  const [state, setState] = useState({
    visibility: user.visibility, is_online: !!user.isOnline, can_show_activity: !!user.canShowActivity,
    can_show_achievements: !!user.canShowAchievements, show_featured_members: !!user.showFeaturedMembers
  })
  const [status, setStatus] = useState(null) // 'saving' | 'saved' | 'failed'

  const change = (patch) => {
    const previous = state
    const next = { ...state, ...patch }
    setState(next)
    setStatus('saving')
    putpostRequest(`/api/v3/profiles/${user.profileId}`, 'PATCH', { profile: patch }, (err) => {
      if (err) { setState(previous); setStatus('failed'); return }
      setUser({ ...user, visibility: next.visibility, isOnline: next.is_online, canShowActivity: next.can_show_activity, canShowAchievements: next.can_show_achievements, showFeaturedMembers: next.show_featured_members })
      setStatus('saved')
      setTimeout(() => setStatus(null), 2000)
    })
  }

  return <div className='md:grid md:grid-cols-4 md:gap-6'>
    <div className='mb-4 md:mb-0'>
      <h3 className='text-lg font-semibold text-gray-900 dark:text-gray-100'>Who can see your profile</h3>
      <p className='mt-1 text-sm text-gray-600 dark:text-gray-300'>Changes here save on their own.</p>
    </div>
    <Card header={<span className={classNames('text-xs font-semibold', status === 'failed' ? 'text-red-600' : 'text-green-600 dark:text-green-300')}>
      { status === 'saving' && 'Saving...' }{ status === 'saved' && <span className='inline-flex items-center gap-x-1'><CheckCircleIcon className='h-4 w-4' /> Saved</span> }{ status === 'failed' && "That didn't save. Try again." }
    </span>}>
      <div className='grid gap-2 sm:grid-cols-3'>
        { VISIBILITY.map((v) => (
          <button key={v.value} type='button' onClick={() => change({ visibility: v.value })} aria-pressed={state.visibility === v.value}
            className={classNames('flex items-start gap-x-2 rounded-lg border-2 p-3 text-left focus:outline-none focus:ring-2 focus:ring-cccblue',
              state.visibility === v.value ? 'border-cccblue bg-cccblue/10' : 'border-gray-200 hover:border-gray-300 dark:border-gray-600')}>
            <v.icon className='mt-0.5 h-5 w-5 flex-shrink-0 text-cccblue' />
            <span><span className='block text-sm font-semibold text-gray-900 dark:text-white'>{v.label}</span><span className='block text-xs text-gray-600 dark:text-gray-300'>{v.blurb}</span></span>
          </button>
        ))}
      </div>
      <div className='divide-y divide-gray-200 dark:divide-gray-700'>
        <Toggle checked={state.is_online} onChange={(v) => change({ is_online: v })} label='Online indicator' hint='Show a green dot when you are on the site.' />
        <Toggle checked={state.can_show_activity} onChange={(v) => change({ can_show_activity: v })} label='Recent activity' hint='Comments, submissions, and new roles show on your profile.' />
        <Toggle checked={state.can_show_achievements} onChange={(v) => change({ can_show_achievements: v })} label='Achievements' hint='Show your site achievements on your profile.' />
        <Toggle checked={state.show_featured_members} onChange={(v) => change({ show_featured_members: v })} locked={!user.canHideUserSidebarSuggestions}
          label='Featured members in your sidebar'
          hint={user.canHideUserSidebarSuggestions ? 'Other members are suggested beside your profile.' : <>Hiding these is a <Link to='/subscriptions' className='underline'>Premium</Link> perk.</>} />
      </div>
    </Card>
  </div>
}

export default ProfileOptions
